import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { NetworkConfig, NETWORKS, getNetworkByChainId, getSupportedNetworks, DEFAULT_NETWORK } from '../config/networks';

interface NetworkContextType {
  currentNetwork: NetworkConfig | null;
  supportedNetworks: NetworkConfig[];
  switchNetwork: (networkKey: string) => Promise<void>;
  addNetwork: (network: NetworkConfig) => Promise<void>;
  isNetworkSupported: (chainId: number) => boolean;
  isLoading: boolean;
  error: string | null;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

interface NetworkProviderProps {
  children: ReactNode;
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({ children }) => {
  const [currentNetwork, setCurrentNetwork] = useState<NetworkConfig | null>(null);
  const [supportedNetworks, setSupportedNetworks] = useState<NetworkConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize supported networks
  useEffect(() => {
    const networks = getSupportedNetworks();
    setSupportedNetworks(networks);
    
    // Don't set a default network immediately - wait for actual network detection
    // This prevents the race condition where the UI shows the wrong network briefly
  }, []);

  // Listen for network changes
  useEffect(() => {
    if (window.ethereum) {
      const handleChainChanged = (chainId: string) => {
        const network = getNetworkByChainId(parseInt(chainId, 16));
        if (network) {
          setCurrentNetwork(network);
          setError(null);
        } else {
          setError(`Unsupported network with chain ID: ${chainId}`);
        }
      };

      window.ethereum.on('chainChanged', handleChainChanged);

      // Check initial network
      if (window.ethereum.chainId) {
        handleChainChanged(window.ethereum.chainId);
      } else {
        // If no chainId is available immediately, try to get it asynchronously
        window.ethereum.request({ method: 'eth_chainId' })
          .then((chainId: string) => {
            handleChainChanged(chainId);
          })
          .catch((error) => {
            console.warn('Failed to get initial chain ID:', error);
            // Set a fallback network only if we can't detect the current one
            const networks = getSupportedNetworks();
            if (networks.length > 0) {
              const defaultNetwork = networks.find(n => n.name === DEFAULT_NETWORK) || networks[0];
              setCurrentNetwork(defaultNetwork);
            }
          });
      }

      return () => {
        window.ethereum?.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []);

  const switchNetwork = async (networkKey: string): Promise<void> => {
    const network = NETWORKS[networkKey];
    if (!network) {
      throw new Error(`Network ${networkKey} not found`);
    }

    if (!window.ethereum) {
      throw new Error('MetaMask not installed');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Try to switch to the network
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${network.chainId.toString(16)}` }],
      });
      setCurrentNetwork(network);
    } catch (switchError: any) {
      // If the network doesn't exist, try to add it
      if (switchError.code === 4902) {
        await addNetwork(network);
      } else {
        throw switchError;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addNetwork = async (network: NetworkConfig): Promise<void> => {
    if (!window.ethereum) {
      throw new Error('MetaMask not installed');
    }

    try {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: `0x${network.chainId.toString(16)}`,
          chainName: network.name,
          rpcUrls: [network.rpcUrl],
          blockExplorerUrls: [network.blockExplorer],
          nativeCurrency: network.nativeCurrency,
        }],
      });
      setCurrentNetwork(network);
    } catch (error) {
      throw new Error(`Failed to add network: ${(error as Error).message}`);
    }
  };

  const isNetworkSupported = (chainId: number): boolean => {
    return supportedNetworks.some(network => network.chainId === chainId);
  };

  const value: NetworkContextType = {
    currentNetwork,
    supportedNetworks,
    switchNetwork,
    addNetwork,
    isNetworkSupported,
    isLoading,
    error,
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = (): NetworkContextType => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
